import type { LogBackend } from "../backends/interface";
import type { ContextProvider, CoreFieldsInput } from "../core-fields";
import { Logger } from "../logger";
import { setRedactedRoutes } from "../current-path";

// Empty string counts as absent: that is how a declared-but-unset env var arrives, and ingest wants a real version or no field.
export function clientCoreFields(
  service: string,
  environment: string,
  version: unknown,
): CoreFieldsInput {
  return {
    service,
    environment,
    version:
      typeof version === "string" && version !== "" ? version : undefined,
  };
}

export interface ClientLoggingConfig {
  readonly coreFields: CoreFieldsInput;
  readonly debug: boolean;
  readonly getBackend: () => LogBackend;
  readonly getSessionId: () => string;
  readonly getVisitId: () => string;
  readonly redactedRoutes?: readonly string[];
}

export interface ClientLoggerContext {
  userId?: string;
  sessionId?: string;
  // Explicit because recordLanding emits while resolving the lead, before LoggerProvider has a context to read it from.
  leadId?: string;
}

export interface ClientLogging {
  readonly createLogger: (contextProvider: ContextProvider) => Logger;
  // No memoization: the id is stable for the page load even without storage (see `mintedForThisLoad`), so every logger agrees with LoggerProvider's.
  readonly createClientLogger: (context?: ClientLoggerContext) => Logger;
  readonly getSessionId: () => string;
}

export function createClientLogging(
  config: ClientLoggingConfig,
): ClientLogging {
  setRedactedRoutes(config.redactedRoutes ?? []);
  // Delegate so class-based providers retain their prototype methods.
  const withVisitId = (contextProvider: ContextProvider): ContextProvider => ({
    getSessionId: () => contextProvider.getSessionId(),
    getUserId: () => contextProvider.getUserId(),
    getReaderId: () => contextProvider.getReaderId?.(),
    getLeadId: () => contextProvider.getLeadId?.(),
    getVisitId: config.getVisitId,
  });

  const createLogger = (contextProvider: ContextProvider): Logger =>
    new Logger(
      config.getBackend(),
      withVisitId(contextProvider),
      config.coreFields,
      config.debug,
    );

  return {
    createLogger,
    createClientLogger: (context) =>
      createLogger(
        makeContextProvider(
          context?.sessionId ?? config.getSessionId(),
          context?.userId,
          context?.leadId,
        ),
      ),
    getSessionId: config.getSessionId,
  };
}

export function makeContextProvider(
  sessionId: string,
  userId?: string,
  leadId?: string,
): ContextProvider {
  return {
    getSessionId: () => sessionId,
    getUserId: () => userId,
    getLeadId: () => leadId,
  };
}
